import { Component } from '@theme/component';

/* GSAP loads once from CDN, same shared-module-cache pattern as the PDP's
   note cards — a real network fetch only the first time any page on the
   site needs it this session, not once per page. */
let gsapPromise = null;
function loadGsap() {
  if (!gsapPromise) gsapPromise = import('https://cdn.jsdelivr.net/npm/gsap@3.15.0/index.js');
  return gsapPromise;
}
let customEasePromise = null;
function loadCustomEase() {
  if (!customEasePromise) customEasePromise = import('https://cdn.jsdelivr.net/npm/gsap@3.15.0/CustomEase.js');
  return customEasePromise;
}

/* Two signature curves shared by every tween in this component, in place of
   GSAP's built-in power2 eases — built-in eases read as "generic library
   default" at this level of polish (Emil Kowalski's own animation standard
   flags them as too weak for deliberate UI motion). Values are the strong
   ease-out / ease-in-out pair from that same standard, not hand-guessed. */
let EASE_OUT = 'power2.out';
let EASE_IN_OUT = 'power2.inOut';
function registerBrandEases(gsap, CustomEase) {
  gsap.registerPlugin(CustomEase);
  EASE_OUT = CustomEase.create('finder-out', '0.23, 1, 0.32, 1');
  EASE_IN_OUT = CustomEase.create('finder-in-out', '0.77, 0, 0.175, 1');
}

const VERT_SRC = `#version 300 es
layout(location=0) in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`;

/* Intimate pool of warm light in a dim room (slow smoke, one breathing glow). Earlier note: a slow warm-white drift — reference-checked against D&G's own gift-finder
   recording: their ambient field is a LIGHT, glowing cream field (not the
   dark gold-on-brown this page used before), so the palette here is
   inverted from the PDP's dark liquid-gold background rather than reused
   from it. Same fbm noise technique, different tonal range entirely. */
const FRAG_SRC = `#version 300 es
precision highp float;
uniform vec2 uResolution;
uniform float uTime;
out vec4 outColor;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 2; i++) { v += a * snoise(p); p *= 2.02; a *= 0.5; }
  return v;
}

vec3 palette(float t) {
  const vec3 p0 = vec3(0.961, 0.929, 0.882);
  const vec3 p1 = vec3(0.973, 0.949, 0.914);
  const vec3 p2 = vec3(0.902, 0.855, 0.792);
  const vec3 p3 = vec3(0.831, 0.776, 0.718);
  const vec3 p4 = vec3(0.675, 0.608, 0.541);
  t = clamp(t, 0.0, 1.0);
  vec3 c = mix(p0, p1, smoothstep(0.0, 0.3, t));
  c = mix(c, p2, smoothstep(0.25, 0.55, t));
  c = mix(c, p3, smoothstep(0.5, 0.8, t));
  return mix(c, p4, smoothstep(0.75, 1.0, t));
}

// "Slow Drift": the menu atmosphere (cream, warped smoke) at 0.4x speed.
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float t = uTime * 0.4;
  vec2 p = uv * 1.1;
  p.x *= 1.6;
  vec2 warp = vec2(fbm(p * 1.3 + vec2(0.0, t * 0.02)), fbm(p * 1.3 - vec2(t * 0.015, 0.0)));
  vec2 warped = p + warp * 0.35;
  float density = fbm(warped * 1.1 + t * 0.008);
  float drape = fbm(vec2(warped.x * 2.4, warped.y * 0.6) - t * 0.006);
  float illum = clamp(density * 0.38 + drape * 0.28 + 0.5, 0.0, 1.0);
  vec3 color = palette(illum);

  outColor = vec4(color, 1.0);
}`;

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return shader;
}

/* ------------------------------------------------------------------ *
 * Data helpers. Every product carries its own scent profile in the     *
 * custom.classification metafield (family, occasion, season,           *
 * character: five labelled values each, plus the product's own         *
 * sentence for each). Labels differ per product, so everything is      *
 * looked up by normalised label, never by position.                    *
 * ------------------------------------------------------------------ */

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

function toMap(block) {
  const out = {};
  if (!block || !block.labels) return out;
  block.labels.forEach((label, i) => { out[norm(label)] = Number(block.data[i]) || 0; });
  return out;
}

function sentence(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

function shortName(p) {
  const raw = (p.short || p.title || '').trim();
  return raw.replace(/\s+(extrait|parfum)\b.*$/i, '').trim() || raw;
}

/* Cloth -> scent-family map. Weights say how strongly a family label in a
   product's own profile counts as "this cloth". Our own mapping, kept in
   plain sight so a perfumer can retune it. */
const FAMILIES = {
  leather: { woody: 1, leathery: 1, smoky: 0.8, oud: 1, earthy: 0.8, resinous: 0.7, chypre: 0.5, oriental: 0.4 },
  linen: { fresh: 1, green: 1, citrus: 1, aromatic: 0.9, fougere: 0.9, aquatic: 0.8 },
  silk: { floral: 1, powdery: 0.9, creamy: 0.7, fruity: 0.4, chypre: 0.4 },
  cashmere: { sweet: 1, oriental: 0.9, gourmand: 0.9, creamy: 0.6, spicy: 0.6, resinous: 0.5, fruity: 0.3 },
  // what a visitor might refuse
  rSweet: { sweet: 1, gourmand: 1, fruity: 0.8, creamy: 0.7 },
  rSmoke: { smoky: 1, resinous: 0.9, oud: 1, animalic: 1, leathery: 0.8 },
  rFlower: { floral: 1, powdery: 0.9 },
  rSharp: { green: 1, citrus: 1, fougere: 0.8, aromatic: 0.8 },
};

/* Six questions, three stations: the opening, the heart, the base, the
   same three movements as every pyramid in the house. Choices are
   described in hours, weather and cloth, never in notes, because nobody
   can answer "do you like galbanum" but everyone knows what they would
   pull from the wardrobe. `whisper` is the single line that holds the
   screen for a moment after a choice. */
const STATIONS = ['I · The Opening', 'II · The Heart', 'III · The Base'];

const QUESTIONS = [
  {
    key: 'hour', station: 0, kicker: 'The hour',
    prompt: 'When is it yours?',
    options: [
      { name: 'The first hour', note: 'Before the day asks anything', whisper: 'Before the day asks anything.', occ: { daily: 1, leisure: 0.3 } },
      { name: 'The working day', note: 'Quiet authority', whisper: 'Authority, worn quietly.', occ: { business: 1, daily: 0.5 } },
      { name: 'A long afternoon', note: 'Nowhere to be', whisper: 'Nowhere to be, and no hurry.', occ: { leisure: 1, daily: 0.4 } },
      { name: 'Dinner at eight', note: 'Candlelight, a good table', whisper: 'Candlelight, and a good table.', occ: { evening: 1, 'night out': 0.4 } },
      { name: 'After midnight', note: 'When the room changes', whisper: 'When the room begins to change.', occ: { 'night out': 1, evening: 0.5 } },
    ],
  },
  {
    key: 'weather', station: 0, kicker: 'The weather',
    prompt: 'Where does your mind go first?',
    options: [
      { name: 'A low fire, heavy curtains', note: 'Winter, kept out', whisper: 'The cold kept politely outside.', sea: { winter: 1, fall: 0.5 } },
      { name: 'Golden light, turning leaves', note: 'Autumn afternoon', whisper: 'The season of decisions.', sea: { fall: 1, winter: 0.3, spring: 0.3 } },
      { name: 'Rain on warm stone', note: 'The air after', whisper: 'Everything washed, nothing lost.', sea: { rainy: 1, spring: 0.6 } },
      { name: 'High sun, open water', note: 'Summer, unhurried', whisper: 'Salt, light, and the whole day ahead.', sea: { summer: 1, spring: 0.4 } },
    ],
  },
  {
    key: 'cloth', station: 1, kicker: 'The cloth',
    prompt: 'Reach into the wardrobe. What do you pull out?',
    options: [
      { name: 'Worn leather', note: 'Dark wood, a little smoke', whisper: 'Softened by years of use.', fam: 'leather' },
      { name: 'Raw linen', note: 'Cut grass, open windows', whisper: 'Clean, and slightly undone.', fam: 'linen' },
      { name: 'Silk', note: 'Petals, powder, skin', whisper: 'Cool at first, then warm.', fam: 'silk' },
      { name: 'Cashmere', note: 'Amber, spice, something sweet', whisper: 'The kind you keep for yourself.', fam: 'cashmere' },
    ],
  },
  {
    key: 'presence', station: 1, kicker: 'The distance',
    prompt: 'How near should it stay?',
    options: [
      { name: 'A trace', note: 'For whoever leans in', whisper: 'Found only by those who come close.', target: 45, hold: 'kept close to the skin' },
      { name: 'Present, never announced', note: 'Felt before it is noticed', whisper: 'Felt before it is noticed.', target: 65, hold: 'within arm’s reach' },
      { name: 'The room remembers', note: 'Long after you have left', whisper: 'Remembered after you have gone.', target: 85, hold: 'left to fill the room' },
    ],
  },
  {
    key: 'arrival', station: 2, kicker: 'The entrance',
    prompt: 'How do you arrive?',
    options: [
      { name: 'Magnetic', note: 'Heads turn, slowly', whisper: 'Slowly, and without effort.', chr: 'magnetic' },
      { name: 'Intimate', note: 'Only those close notice', whisper: 'A secret, shared with few.', chr: 'intimate' },
      { name: 'Grounded', note: 'Calm, no performance', whisper: 'Nothing to prove.', chr: 'grounded' },
      { name: 'Fluid', note: 'Different every time', whisper: 'Never the same twice.', chr: 'fluid' },
      { name: 'Commanding', note: 'Before you speak', whisper: 'Before a word is said.', chr: 'commanding' },
    ],
  },
  {
    key: 'refusal', station: 2, kicker: 'The refusal',
    prompt: 'Is there anything you would rather not meet?',
    options: [
      { name: 'Too sweet', note: 'Sugar, cream, fruit', whisper: 'Noted. Nothing cloying.', avoid: 'rSweet' },
      { name: 'Smoke and resin', note: 'Char, incense, animal warmth', whisper: 'Noted. The fire stays out.', avoid: 'rSmoke' },
      { name: 'Heavy flowers', note: 'Powder and petals', whisper: 'Noted. Nothing perfumed-looking.', avoid: 'rFlower' },
      { name: 'Sharp and green', note: 'Citrus peel, cut stems', whisper: 'Noted. Nothing sharp.', avoid: 'rSharp' },
      { name: 'Nothing at all', note: 'Surprise me', whisper: 'Then we begin with an open hand.', avoid: null },
    ],
  },
];

const THINKING_MS = 1250;

/* ------------------------------------------------------------------ *
 * Matching. Weighted blend of five profile fits minus a refusal        *
 * penalty. Deterministic, runs in the browser, uses only the data the  *
 * products already carry. Every term can be explained in the           *
 * product's own words, so nothing here is a black box.                 *
 * ------------------------------------------------------------------ */

const WEIGHT = { hour: 0.22, weather: 0.18, cloth: 0.30, presence: 0.12, arrival: 0.18 };
const PENALTY = 0.30;

function scoreProduct(p, a) {
  const cls = p.cls || {};
  const occ = toMap(cls.occasion);
  const sea = toMap(cls.season);
  const typ = toMap(cls.type);
  const chr = toMap(cls.character);
  const typeTotal = Object.values(typ).reduce((s, v) => s + v, 0) || 1;

  const fit = (profile, weights) => {
    let num = 0; let den = 0;
    for (const k in weights) { num += weights[k] * ((profile[k] || 0) / 10); den += weights[k]; }
    return den ? num / den : 0;
  };
  const share = (family) => {
    let s = 0;
    for (const k in family) s += family[k] * (typ[k] || 0);
    return Math.min(1, (s / typeTotal) * 1.5);
  };

  const proj = Number(p.int && p.int.projection && p.int.projection.pct) || 60;
  const terms = {
    hour: fit(occ, a.hour.occ),
    weather: fit(sea, a.weather.sea),
    cloth: share(FAMILIES[a.cloth.fam]),
    presence: Math.max(0, 1 - Math.abs(proj - a.presence.target) / 55),
    arrival: (chr[a.arrival.chr] || 0) / 10,
  };
  const refused = a.refusal.avoid ? share(FAMILIES[a.refusal.avoid]) : 0;
  const penalty = Math.min(1, refused * 1.6);

  let score = -PENALTY * penalty;
  for (const k in WEIGHT) score += WEIGHT[k] * terms[k];
  return { p, terms, penalty, score, lead: Object.keys(typ).sort((x, y) => typ[y] - typ[x])[0] || '' };
}

/* Hero, two alternatives from other families (so they are not near
   duplicates), and one deliberate contrast: strong on the hour and the
   weather, weak on the cloth they chose. */
function compose(products, a) {
  const scored = products.map((p) => scoreProduct(p, a)).sort((x, y) => y.score - x.score);
  const hero = scored[0];
  const alt = [];
  const usedLead = new Set([hero.lead]);
  for (const s of scored.slice(1)) {
    if (alt.length >= 2) break;
    if (usedLead.has(s.lead)) continue;
    alt.push(s); usedLead.add(s.lead);
  }
  for (const s of scored.slice(1)) {
    if (alt.length >= 2) break;
    if (!alt.includes(s)) alt.push(s);
  }
  const rest = scored.filter((s) => s !== hero && !alt.includes(s));
  let contrast = null;
  let best = -Infinity;
  for (const s of rest) {
    const c = (s.terms.hour + s.terms.weather) * 0.5 - s.terms.cloth * 1.2 - s.penalty;
    if (c > best) { best = c; contrast = s; }
  }
  return { hero, alt, contrast };
}

/* "Built on Woody and Oriental, with Smoky beneath." from the product's own
   family profile, so this reason never repeats the headline sentence. */
function familyReading(type) {
  if (!type || !type.labels) return '';
  const ranked = type.labels
    .map((label, i) => ({ label, v: Number(type.data[i]) || 0 }))
    .sort((x, y) => y.v - x.v)
    .filter((r) => r.v > 0);
  if (ranked.length < 2) return '';
  const head = `Built on ${ranked[0].label} and ${ranked[1].label}`;
  return ranked[2] ? `${head}, with ${ranked[2].label} beneath.` : `${head}.`;
}

/* Why it matched: the three terms that carried the most weight, each
   spoken in the product's own sentence from its metafields. */
function reasonsFor(s, a) {
  const cls = s.p.cls || {};
  const int = s.p.int || {};
  const all = [
    { k: 'hour', label: 'The hour', choice: a.hour.name, text: sentence(cls.occasion && cls.occasion.line) },
    { k: 'weather', label: 'The weather', choice: a.weather.name, text: sentence(cls.season && cls.season.line) },
    { k: 'cloth', label: 'The cloth', choice: a.cloth.name, text: familyReading(cls.type) },
    { k: 'presence', label: 'The distance', choice: a.presence.name, text: sentence(int.projection && int.projection.desc) },
    {
      k: 'arrival', label: 'The entrance', choice: a.arrival.name,
      text: [cls.character && cls.character.compound, sentence(cls.character && cls.character.line)].filter(Boolean).join('. ').replace(/\.\./g, '.'),
    },
  ].filter((r) => r.text);
  all.sort((x, y) => (WEIGHT[y.k] * s.terms[y.k]) - (WEIGHT[x.k] * s.terms[x.k]));
  return all.slice(0, 3);
}

/* ------------------------------------------------------------------ *
 * DOM helper                                                           *
 * ------------------------------------------------------------------ */

function el(tag, cls, text) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

/** Wraps each word in a clipped mask so a headline can rise into place. */
function splitWords(node, text) {
  node.textContent = '';
  const inners = [];
  text.split(/\s+/).forEach((word, i, arr) => {
    const mask = el('span', 'fragrance-finder__w');
    const inner = el('span', 'fragrance-finder__wi', word);
    mask.appendChild(inner);
    node.appendChild(mask);
    if (i < arr.length - 1) node.appendChild(document.createTextNode(' '));
    inners.push(inner);
  });
  return inners;
}

/**
 * Fragrance Finder: a private consultation, not a chat. One question per
 * screen, typographic choices, a thinking beat, then a staged reveal of
 * a single composition with its reasons.
 * @extends {Component}
 */
class FragranceFinderComponent extends Component {
  // Not using requiredRefs: this module can run while the browser is still
  // streaming the component's children (it is async and cached on repeat
  // visits), and the base class would throw on refs that simply have not
  // been parsed yet. Setup waits for the document to finish parsing instead.
  connectedCallback() {
    super.connectedCallback();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
    } else {
      this._init();
    }
  }

  _init() {
    if (this._ready) return;
    // Own ref lookup: the base class fills this.refs from its own
    // connectedCallback, which can run before this component's children have
    // been parsed (module scripts are async), leaving every ref empty and the
    // Start button wired to nothing. Collecting here, after parsing, cannot race.
    const R = {};
    this.querySelectorAll('[ref]').forEach((node) => {
      const name = node.getAttribute('ref');
      if (name.endsWith('[]')) (R[name.slice(0, -2)] ||= []).push(node);
      else R[name] = node;
    });
    this.R = R;
    const needed = [
      'canvas', 'stations', 'restartBtn', 'landing', 'startBtn', 'stage', 'station', 'prompt', 'choices',
      'whisper', 'backBtn', 'thinking', 'orb', 'thinkingLine', 'resultPanel', 'resultInner', 'products',
      'discovery', 'allUrl',
    ];
    const missing = needed.filter((name) => !this.R[name]);
    if (missing.length) {
      console.warn('fragrance-finder: missing refs', missing.join(', '));
      return;
    }
    this._ready = true;

    const parse = (ref, fallback) => {
      try { return JSON.parse(this.R[ref].textContent || 'null') ?? fallback; } catch { return fallback; }
    };
    this._products = parse('products', []);
    this._discovery = parse('discovery', null);
    this._allUrl = parse('allUrl', '/collections/all');
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._gsap = null;
    this._timers = [];
    this._view = 'landing';

    this._resetAnswers();

    this.R.startBtn.addEventListener('click', () => this._begin());
    this.R.restartBtn.addEventListener('click', () => this._begin());
    this.R.backBtn.addEventListener('click', () => this._back());
    this._onKey = (event) => this._handleKey(event);
    document.addEventListener('keydown', this._onKey);

    loadGsap()
      .then(({ gsap }) => {
        this._gsap = gsap;
        return loadCustomEase().then(({ CustomEase }) => registerBrandEases(gsap, CustomEase));
      })
      .catch(() => {});

    this._playIntro();

    this._initGL();
    if (this._gl) {
      this._resizeObserver = new ResizeObserver(() => this._resize());
      this._resizeObserver.observe(this);
      this._resize();
      this._onVisibility = () => (document.hidden ? this._stopLoop() : this._startLoop());
      document.addEventListener('visibilitychange', this._onVisibility);
      this._startLoop();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this._ready) return;
    this._stopLoop();
    this._resizeObserver?.disconnect();
    document.removeEventListener('keydown', this._onKey);
    document.removeEventListener('visibilitychange', this._onVisibility);
    this._clearTimers();
    this._stopDust();
    this._orb?.destroy?.();
  }

  /* Entrance: the landing rises into place once GSAP is ready. The hidden
     starting state only applies while that is pending; a timer always
     releases it, so the page can never stay blank if GSAP fails to load. */
  _playIntro() {
    const landing = this.R.landing;
    if (this._reducedMotion) return;
    landing.classList.add('is-intro');
    const release = () => landing.classList.remove('is-intro');
    this._later(release, 3000);
    loadGsap().then(({ gsap }) => {
      const parts = Array.from(landing.children);
      gsap.set(parts, { opacity: 0, y: 22 });
      release();
      gsap.to(parts, { opacity: 1, y: 0, duration: 1.4, stagger: 0.16, ease: 'power3.out', delay: 0.15 });
    }).catch(release);
  }

  _later(fn, ms) {
    const id = window.setTimeout(fn, ms);
    this._timers.push(id);
    return id;
  }

  _clearTimers() {
    for (const id of this._timers) window.clearTimeout(id);
    this._timers = [];
  }

  _resetAnswers() {
    this._answers = {};
    this._picked = []; // option names, in order
    this._index = 0;
  }

  /* ---------- flow ---------- */

  _begin() {
    this._clearTimers();
    this._stopDust();
    this._gsap?.killTweensOf('*');
    this._resetAnswers();
    this.R.resultPanel.hidden = true;
    this.R.resultInner.textContent = '';
    this.R.thinking.hidden = true;
    this.R.restartBtn.hidden = false;
    const showStage = () => {
      this.R.landing.hidden = true;
      this.R.stage.hidden = false;
      this._view = 'stage';
      this._renderQuestion(0);
    };
    if (this._view === 'landing' && this._gsap && !this._reducedMotion) {
      this._gsap.to(this.R.landing, { opacity: 0, y: -12, duration: 0.7, ease: EASE_OUT, onComplete: showStage });
      this._later(() => { if (!this.R.landing.hidden) showStage(); }, 1200);
    } else {
      showStage();
    }
  }

  _markStation(station) {
    this.R.stations.querySelectorAll('li').forEach((li) => {
      const n = Number(li.dataset.station);
      li.classList.toggle('is-current', n === station);
      li.classList.toggle('is-done', n < station);
    });
  }

  _renderQuestion(index) {
    const q = QUESTIONS[index];
    this._index = index;
    this._markStation(q.station);

    const { station, prompt, choices, whisper, backBtn } = this.R;
    station.textContent = `${STATIONS[q.station]} — ${q.kicker}`;
    prompt.id = 'fragrance-finder-prompt';
    whisper.textContent = '';
    whisper.classList.remove('is-on');
    backBtn.hidden = index === 0;

    const words = splitWords(prompt, q.prompt);
    choices.textContent = '';
    choices.classList.remove('is-settled');
    const rows = q.options.map((opt, i) => {
      const btn = el('button', 'fragrance-finder__choice');
      btn.type = 'button';
      btn.dataset.index = String(i);
      btn.append(
        el('span', 'fragrance-finder__choice-num', String(i + 1).padStart(2, '0')),
        el('span', 'fragrance-finder__choice-name', opt.name),
        el('span', 'fragrance-finder__choice-note', opt.note),
      );
      btn.addEventListener('click', () => this._choose(index, i, btn));
      choices.appendChild(btn);
      return btn;
    });

    const gsap = this._gsap;
    if (!gsap || this._reducedMotion) {
      [station, ...words, ...rows].forEach((n) => { n.style.opacity = ''; n.style.transform = ''; });
      prompt.focus({ preventScroll: true });
      return;
    }
    gsap.set([station, ...rows], { opacity: 0 });
    gsap.set(words, { yPercent: 115 });
    gsap.set(rows, { y: 16 });
    const tl = gsap.timeline({ defaults: { ease: EASE_OUT } });
    tl.to(station, { opacity: 1, duration: 0.8 })
      .to(words, { yPercent: 0, duration: 1.05, stagger: 0.055 }, '<0.1')
      .to(rows, { opacity: 1, y: 0, duration: 0.9, stagger: 0.09 }, '<0.45')
      .call(() => prompt.focus({ preventScroll: true }));
  }

  _choose(index, i, btn) {
    if (this.R.choices.classList.contains('is-settled')) return;
    const q = QUESTIONS[index];
    const opt = q.options[i];
    this._answers[q.key] = opt;
    this._picked[index] = opt.name;

    const { choices, whisper } = this.R;
    choices.classList.add('is-settled');
    choices.querySelectorAll('button').forEach((b) => {
      b.disabled = true;
      b.classList.toggle('is-picked', b === btn);
    });
    whisper.textContent = opt.whisper;
    whisper.classList.add('is-on');

    const advance = () => {
      if (index + 1 < QUESTIONS.length) this._renderQuestion(index + 1);
      else this._think();
    };
    if (!this._gsap || this._reducedMotion) {
      this._later(advance, 350);
      return;
    }
    const others = Array.from(choices.querySelectorAll('button')).filter((b) => b !== btn);
    this._gsap.to(others, { opacity: 0.18, filter: 'blur(3px)', duration: 0.8, ease: EASE_OUT });
    this._gsap.fromTo(whisper, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.8, ease: EASE_OUT });
    this._later(() => {
      this._gsap.to([this.R.station, this.R.prompt, choices, whisper], {
        opacity: 0, y: -10, duration: 0.65, ease: EASE_IN_OUT,
        onComplete: () => {
          this._gsap.set([this.R.station, this.R.prompt, choices, whisper], { clearProps: 'all' });
          advance();
        },
      });
    }, 1350);
  }

  _back() {
    if (this._index === 0) return;
    this._clearTimers();
    this._gsap?.killTweensOf('*');
    const q = QUESTIONS[this._index - 1];
    delete this._answers[q.key];
    this._picked.length = this._index - 1;
    [this.R.station, this.R.prompt, this.R.choices, this.R.whisper].forEach((n) => {
      n.style.opacity = ''; n.style.transform = ''; n.style.filter = '';
    });
    this._renderQuestion(this._index - 1);
  }

  _handleKey(event) {
    if (this._view !== 'stage' || event.metaKey || event.ctrlKey || event.altKey) return;
    const choices = Array.from(this.R.choices.querySelectorAll('button'));
    if (!choices.length || this.R.choices.classList.contains('is-settled')) return;
    if (/^[1-9]$/.test(event.key)) {
      const target = choices[Number(event.key) - 1];
      if (target) { event.preventDefault(); target.click(); }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const at = choices.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? (at + 1) % choices.length : (at <= 0 ? choices.length - 1 : at - 1);
      choices[next].focus();
    }
  }

  /* ---------- thinking ---------- */

  _think() {
    this._view = 'thinking';
    this.R.stage.hidden = true;
    this.R.thinking.hidden = false;
    this._markStation(3);

    const a = this._answers;
    const lines = [
      `${a.hour.name}, set against ${a.weather.name.charAt(0).toLowerCase()}${a.weather.name.slice(1)}.`,
      `${a.cloth.name}, ${a.presence.hold}.`,
      a.refusal.avoid ? `Leaving out ${a.refusal.name.toLowerCase()}.` : 'Leaving the door open.',
    ];

    const orbCanvas = this.R.orb;
    if (!this._orb && window.ThinkingOrbs) {
      try {
        this._orb = window.ThinkingOrbs.create(orbCanvas, { state: 'breathing', size: 220, theme: 'light', speed: 0.55 });
      } catch { this._orb = null; }
    }
    this._orb?.setState?.('breathing');
    this._orb?.setPaused?.(false);

    const line = this.R.thinkingLine;
    const show = (text) => {
      line.textContent = text;
      if (this._gsap && !this._reducedMotion) {
        this._gsap.fromTo(line, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.7, ease: EASE_OUT });
      }
    };
    if (this._gsap && !this._reducedMotion) {
      this._gsap.fromTo(this.R.thinking, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: EASE_OUT });
    }
    const gap = this._reducedMotion ? 150 : THINKING_MS;
    show(lines[0]);
    this._later(() => show(lines[1]), gap);
    this._later(() => { this._orb?.setState?.('composing'); show(lines[2]); }, gap * 2);
    this._later(() => this._reveal(), gap * 3 + 200);
  }

  /* ---------- result ---------- */

  _reveal() {
    if (!this._products.length) {
      this._renderEmpty();
      return;
    }
    const a = this._answers;
    const { hero, alt, contrast } = compose(this._products, a);
    const reasons = reasonsFor(hero, a);

    const inner = this.R.resultInner;
    inner.textContent = '';
    inner.appendChild(this._buildResult(hero, alt, contrast, reasons, a));

    this.R.thinking.hidden = true;
    this._orb?.setPaused?.(true);
    this.R.resultPanel.hidden = false;
    this.R.resultPanel.scrollTop = 0;
    this._view = 'result';
    const name = inner.querySelector('.fragrance-finder__result-name');
    name?.focus({ preventScroll: true });

    const bottle = inner.querySelector('.fragrance-finder__result-bottle');
    this._startDust(hero.p.mood && hero.p.mood.particles, hero.p.int && hero.p.int.inkColors);

    const gsap = this._gsap;
    const beats = Array.from(inner.querySelectorAll('[data-beat]'));
    if (!gsap || this._reducedMotion) { beats.forEach((n) => { n.style.opacity = '1'; }); return; }
    const pool = inner.querySelector('.fragrance-finder__result-pool');
    gsap.set(beats, { opacity: 0, y: 18 });
    gsap.set(pool, { opacity: 0, scale: 0.6 });
    gsap.set(bottle, { opacity: 0, y: 40, scale: 0.94, filter: 'blur(8px)' });
    const words = splitWords(name, name.dataset.text || name.textContent);
    gsap.set(words, { yPercent: 115 });
    const tl = gsap.timeline({ defaults: { ease: EASE_OUT } });
    tl.to(pool, { opacity: 1, scale: 1, duration: 2.2, ease: EASE_IN_OUT })
      .to(bottle, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 1.6, ease: EASE_IN_OUT }, '<0.2')
      .to(beats[0], { opacity: 1, y: 0, duration: 0.8 }, '<0.5')
      .to(words, { yPercent: 0, duration: 1.2, stagger: 0.09 }, '<0.1')
      .to(beats.slice(1), { opacity: 1, y: 0, duration: 0.9, stagger: 0.18 }, '<0.45');
    gsap.to(bottle, { y: -8, duration: 5.5, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 2.2 });
  }

  _renderEmpty() {
    this.R.thinking.hidden = true;
    const inner = this.R.resultInner;
    inner.textContent = '';
    const wrap = el('div', 'fragrance-finder__empty');
    wrap.append(
      el('h2', 'fragrance-finder__result-name', 'We could not compose a match.'),
      el('p', 'fragrance-finder__result-line', 'Browse the full collection instead.'),
    );
    const link = el('a', 'fragrance-finder__cta', 'Browse the collection');
    link.href = this._allUrl;
    wrap.appendChild(link);
    inner.appendChild(wrap);
    this.R.resultPanel.hidden = false;
    this._view = 'result';
  }

  _buildResult(hero, alt, contrast, reasons, a) {
    const p = hero.p;
    const cls = p.cls || {};
    const int = p.int || {};
    const ink = (int.inkColors && int.inkColors[0]) || '#B4863A';
    const root = el('div', 'fragrance-finder__composition');
    root.style.setProperty('--ff-ink-accent', ink);

    /* visual */
    const visual = el('div', 'fragrance-finder__result-visual');
    visual.appendChild(el('div', 'fragrance-finder__result-pool'));
    const bottle = el('img', 'fragrance-finder__result-bottle');
    bottle.alt = `${shortName(p)}, extrait de parfum`;
    bottle.src = p.image || '';
    bottle.width = 300; bottle.height = 400;
    visual.appendChild(bottle);

    /* copy */
    const copy = el('div', 'fragrance-finder__result-copy');
    const eyebrow = el('p', 'fragrance-finder__eyebrow', 'Your composition');
    eyebrow.dataset.beat = '';
    const name = el('h2', 'fragrance-finder__result-name', shortName(p));
    name.id = 'fragrance-finder-result-name';
    name.tabIndex = -1;
    name.dataset.text = shortName(p);
    const compound = el('p', 'fragrance-finder__result-compound', [cls.type && cls.type.compound, 'Extrait de Parfum'].filter(Boolean).join('  ·  '));
    compound.dataset.beat = '';
    const line = el('p', 'fragrance-finder__result-line', sentence(cls.type && cls.type.line));
    line.dataset.beat = '';

    const why = el('div', 'fragrance-finder__why');
    why.dataset.beat = '';
    why.appendChild(el('h3', 'fragrance-finder__label', 'Why it is yours'));
    const list = el('ol', 'fragrance-finder__reasons');
    reasons.forEach((r) => {
      const li = el('li', 'fragrance-finder__reason');
      const head = el('p', 'fragrance-finder__reason-head');
      head.append(el('span', 'fragrance-finder__reason-label', r.label), el('em', null, r.choice));
      li.append(head, el('p', 'fragrance-finder__reason-text', r.text));
      list.appendChild(li);
    });
    why.appendChild(list);

    const pyramid = this._buildPyramid(p);
    pyramid.dataset.beat = '';
    const meters = this._buildMeters(int);
    meters.dataset.beat = '';

    const memory = el('blockquote', 'fragrance-finder__memory');
    memory.dataset.beat = '';
    if (p.mem && (p.mem.line1 || p.mem.line2)) {
      if (p.mem.line1) memory.appendChild(el('p', null, p.mem.line1));
      if (p.mem.line2) memory.appendChild(el('p', null, p.mem.line2));
    }

    const actions = el('div', 'fragrance-finder__actions');
    actions.dataset.beat = '';
    const cta = el('a', 'fragrance-finder__cta');
    cta.href = p.url;
    cta.append(el('span', null, `Discover ${shortName(p)}`), el('span', 'fragrance-finder__cta-price', p.price));
    actions.appendChild(cta);
    if (this._discovery && this._discovery.url) {
      const sample = el('a', 'fragrance-finder__secondary', 'Not ready for a full bottle? Begin with the Discovery Collection');
      sample.href = this._discovery.url;
      actions.appendChild(sample);
    }

    copy.append(eyebrow, name, compound, line, why, pyramid, meters, memory, actions);
    const hasMemory = memory.childElementCount > 0;
    if (!hasMemory) memory.remove();

    const hero1 = el('div', 'fragrance-finder__result-hero');
    hero1.append(visual, copy);

    /* more */
    const more = el('div', 'fragrance-finder__more');
    more.dataset.beat = '';
    const alts = el('div', 'fragrance-finder__more-col');
    alts.appendChild(el('h3', 'fragrance-finder__label', 'Also consider'));
    alt.forEach((s) => alts.appendChild(this._miniCard(s)));
    more.appendChild(alts);
    if (contrast) {
      const dare = el('div', 'fragrance-finder__more-col');
      dare.appendChild(el('h3', 'fragrance-finder__label', 'The contrast'));
      dare.appendChild(el('p', 'fragrance-finder__more-lede', 'If you would rather be surprised than matched.'));
      dare.appendChild(this._miniCard(contrast));
      more.appendChild(dare);
    }

    root.append(hero1, more, this._buildLead(p));
    return root;
  }

  _miniCard(s) {
    const p = s.p;
    const cls = p.cls || {};
    const a = el('a', 'fragrance-finder__mini');
    a.href = p.url;
    a.append(
      el('span', 'fragrance-finder__mini-name', shortName(p)),
      el('span', 'fragrance-finder__mini-compound', (cls.type && cls.type.compound) || ''),
      el('span', 'fragrance-finder__mini-line', sentence((p.mood && p.mood.season) || (cls.type && cls.type.line) || '')),
    );
    return a;
  }

  _buildPyramid(p) {
    const wrap = el('div', 'fragrance-finder__pyramid');
    wrap.appendChild(el('h3', 'fragrance-finder__label', 'The pyramid'));
    [['Top', 'top'], ['Heart', 'heart'], ['Base', 'base']].forEach(([label, key]) => {
      const notes = (p.notes && p.notes[key]) || [];
      if (!notes.length) return;
      const row = el('div', 'fragrance-finder__tier');
      row.appendChild(el('span', 'fragrance-finder__tier-name', label));
      const names = el('ul', 'fragrance-finder__tier-notes');
      notes.slice(0, 8).forEach((n) => {
        const li = el('li', null);
        if (n.i) {
          const img = el('img', 'fragrance-finder__note-img');
          img.src = n.i; img.alt = ''; img.loading = 'lazy'; img.width = 28; img.height = 28;
          li.appendChild(img);
        }
        li.appendChild(el('span', null, n.n));
        names.appendChild(li);
      });
      row.appendChild(names);
      wrap.appendChild(row);
    });
    return wrap;
  }

  _buildMeters(int) {
    const wrap = el('div', 'fragrance-finder__meters');
    [['Projection', int.projection], ['Richness', int.richness]].forEach(([label, m]) => {
      if (!m || m.pct == null) return;
      const row = el('div', 'fragrance-finder__meter');
      const head = el('p', 'fragrance-finder__meter-head');
      head.append(el('span', null, label), el('em', null, m.desc || ''));
      const bar = el('div', 'fragrance-finder__meter-bar');
      const fill = el('span', 'fragrance-finder__meter-fill');
      fill.style.setProperty('--ff-fill', `${Math.max(0, Math.min(100, Number(m.pct)))}%`);
      bar.appendChild(fill);
      row.append(head, bar);
      wrap.appendChild(row);
    });
    return wrap;
  }

  _buildLead(p) {
    const form = el('form', 'fragrance-finder__lead');
    form.dataset.beat = '';
    form.appendChild(el('p', 'fragrance-finder__lead-line', `Keep this composition, and hear first when the house releases something new.`));
    const row = el('div', 'fragrance-finder__lead-row');
    const label = el('label', 'visually-hidden', 'Email address');
    label.htmlFor = 'fragrance-finder-lead-email';
    const input = el('input', 'fragrance-finder__lead-input');
    input.type = 'email'; input.id = 'fragrance-finder-lead-email'; input.required = true;
    input.placeholder = 'your@email.com'; input.autocomplete = 'email';
    const submit = el('button', 'fragrance-finder__lead-submit', 'Keep it');
    submit.type = 'submit';
    row.append(label, input, submit);
    const status = el('p', 'fragrance-finder__lead-status');
    status.setAttribute('aria-live', 'polite');
    form.append(row, status);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = input.value.trim();
      if (!email) return;
      submit.disabled = true;
      status.textContent = 'One moment…';
      try {
        const res = await fetch('/contact#newsletter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            form_type: 'customer',
            utf8: '✓',
            'contact[email]': email,
            'contact[tags]': `newsletter,fragrance-finder,finder-${p.url.split('/').pop()}`,
            'contact[accepts_marketing]': '1',
          }),
        });
        if (!res.ok && res.status >= 400) throw new Error('bad status');
        status.textContent = 'Kept. Thank you.';
        input.disabled = true;
      } catch {
        status.textContent = 'That did not go through. Please try again.';
        submit.disabled = false;
      }
    });
    return form;
  }

  /* ---------- the product's own atmosphere ---------- */

  /* Every product already carries a "dust" recipe (colour, count, speed,
     drift) for its mood section on the product page. The reveal borrows
     it, so each composition arrives in its own air. */
  _startDust(recipe, inkColors) {
    this._stopDust();
    if (this._reducedMotion) return;
    const r = recipe || {};
    const rgb = Array.isArray(r.color) ? r.color : [180, 134, 58];
    const count = Math.max(8, Math.min(46, Number(r.count) || 22));
    const speed = Number(r.speed) || 0.2;
    const size = Array.isArray(r.size) ? r.size : [1, 2.4];
    const canvas = el('canvas', 'fragrance-finder__dust');
    canvas.setAttribute('aria-hidden', 'true');
    this.R.resultPanel.parentElement.insertBefore(canvas, this.R.resultPanel);
    this._dustCanvas = canvas;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const fit = () => {
      canvas.width = Math.ceil(canvas.clientWidth * dpr);
      canvas.height = Math.ceil(canvas.clientHeight * dpr);
    };
    fit();
    const motes = Array.from({ length: count }, () => ({
      x: Math.random(), y: Math.random(),
      r: size[0] + Math.random() * (size[1] - size[0]),
      vx: (Math.random() - 0.5) * speed * 0.0004,
      vy: -(0.3 + Math.random()) * speed * 0.0006,
      ph: Math.random() * Math.PI * 2,
    }));
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(64, now - last); last = now;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const m of motes) {
        m.x += m.vx * dt; m.y += m.vy * dt; m.ph += dt * 0.0006;
        if (m.y < -0.02) { m.y = 1.02; m.x = Math.random(); }
        if (m.x < -0.02) m.x = 1.02; else if (m.x > 1.02) m.x = -0.02;
        const a = 0.28 + 0.22 * Math.sin(m.ph);
        ctx.beginPath();
        ctx.fillStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a.toFixed(3)})`;
        ctx.arc(m.x * canvas.width, m.y * canvas.height, m.r * dpr, 0, Math.PI * 2);
        ctx.fill();
      }
      this._dustRaf = requestAnimationFrame(tick);
    };
    this._dustRaf = requestAnimationFrame(tick);
    this._dustResize = () => fit();
    window.addEventListener('resize', this._dustResize);
  }

  _stopDust() {
    if (this._dustRaf) cancelAnimationFrame(this._dustRaf);
    this._dustRaf = null;
    if (this._dustResize) window.removeEventListener('resize', this._dustResize);
    this._dustResize = null;
    this._dustCanvas?.remove();
    this._dustCanvas = null;
  }

  /* ---------- ambient WebGL background ---------- */

  _initGL() {
    const gl = this.R.canvas.getContext('webgl2');
    if (!gl) return;
    this._gl = gl;

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERT_SRC);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);
    this._program = program;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this._uResolution = gl.getUniformLocation(program, 'uResolution');
    this._uTime = gl.getUniformLocation(program, 'uTime');
    // Named _glStart, not _start: an instance property would shadow any
    // prototype method of the same name.
    this._glStart = performance.now();
  }

  _resize() {
    if (!this._gl) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = Math.ceil(this.offsetWidth * dpr);
    const h = Math.ceil(this.offsetHeight * dpr);
    if (this.R.canvas.width === w && this.R.canvas.height === h) return;
    this.R.canvas.width = w;
    this.R.canvas.height = h;
    this._gl.viewport(0, 0, w, h);
    if (this._reducedMotion) this._drawFrame(this._glStart + 4000);
  }

  _drawFrame(now) {
    const gl = this._gl;
    gl.uniform2f(this._uResolution, this.R.canvas.width, this.R.canvas.height);
    gl.uniform1f(this._uTime, (now - this._glStart) / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  _startLoop() {
    if (this._raf || this._reducedMotion) return;
    const tick = (now) => {
      this._drawFrame(now);
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _stopLoop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }
}

if (!customElements.get('fragrance-finder-component')) {
  customElements.define('fragrance-finder-component', FragranceFinderComponent);
}
